<template>
  <page-wrapper :title="title" :hasPad="true">
    <div class="d-flex justify-content-between">
      <sc-button
        variant="secondary"
        @click="handleClickDuplicate"
      >สร้างแพ็กเกจโดยอ้างอิงจากแพ็กเกจนี้</sc-button>
    </div>
    <div class="wrapper">
      <package-editor
        v-model="form"
        :imageUrl="imageUrl"
        :thumbnailUrl="thumbnailUrl"
        :targets="targets"
        :sellerTradeParties="sellerTradeParties"
        :disabled="isEditorDisabled"
      ></package-editor>
      <div class="d-flex justify-content-between mt-4" v-if="!isEditorDisabled">
        <sc-button disabled variant="danger" @click="handleClickRemove">ลบ</sc-button>
        <sc-button variant="primary" @click="handleClickSave">บันทึก</sc-button>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import { Select as ElSelect, Option as ElOption } from "element-ui";
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import PackageEditor from "../components/PackageEditor";
import { baanRueJaiClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import { toCamelCaseObject } from "@/lib/util";
import DatePicker from "@/lib/components/DatePicker";
import { DateTime } from "luxon";
import { isNil, isEmpty } from "ramda";
import { toOptionalDateTime, toOptionalISOString } from "@/lib/dateTime";
import { createPackageType } from "@/lib/mapper";

export default {
  components: {
    PageWrapper,
    ScButton,
    PackageEditor,
    DatePicker,
    ElSelect,
    ElOption
  },
  data() {
    const title = "รายละเอียด Ruejai Club Sale Package";
    const form = {
      id: null,
      name: "",
      receiptName: "",
      target: "สำหรับบ้าน",
      price: 0,
      billingCycle: 1,
      detail: "",
      termsAndConditions: "",
      sellerTradePartyId: null,
      services: [],
      imageFile: null,
      thumbnailFile: null,
      noteLabel: null,
      originalPrice: null,
      purchaseSuccessTitle: null,
      purchaseSuccessMessage: null,
      expiredAt: null
    };

    const imageUrl = null;
    const targets = [];
    const thumbnailUrl = null;
    const sellerTradeParties = [];

    return {
      title,
      form,
      imageUrl,
      targets,
      thumbnailUrl,
      sellerTradeParties
    };
  },
  computed: {
    isEditorDisabled() {
      return this.$isCallCenter;
    }
  },
  watch: {
    form(form, oldForm) {
      if (this.form.imageFile !== oldForm.imageFile) {
        this.imageUrl = URL.createObjectURL(this.form.imageFile);
      }

      if (this.form.thumbnailFile !== oldForm.thumbnailFile) {
        this.thumbnailUrl = URL.createObjectURL(this.form.thumbnailFile);
      }
    }
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    setupData() {
      this.setupTargets();
      this.setupSellerTradeParties();
      this.setupForm();
    },
    async setupTargets() {
      const targets = await this.fetchTargets();

      this.targets = targets;
    },
    async setupSellerTradeParties() {
      const sellerTradeParties = await this.fetchSellerTradeParties();

      this.sellerTradeParties = sellerTradeParties;
    },
    async setupForm() {
      const data = await this.fetchData();
      const {
        id,
        name,
        receipt_name: receiptName,
        detail,
        target,
        price,
        billing_cycle: billingCycle,
        terms_and_conditions: termsAndConditions,
        seller_trade_party_id: sellerTradePartyId,
        image_url: imageUrl,
        services,
        thumbnail_url: thumbnailUrl,
        note_label: noteLabel,
        registration_form: registrationForm,
        purchasing_limit: purchasingLimit,
        original_price: originalPrice,
        purchase_success_title: purchaseSuccessTitle,
        purchase_success_message: purchaseSuccessMessage,
        expired_at: expiredAt
      } = data;

      this.form = {
        id: id,
        name,
        receiptName,
        detail,
        target,
        price: price / 100,
        originalPrice: !isNil(originalPrice)
          ? originalPrice / 100
          : null,
        billingCycle,
        termsAndConditions,
        sellerTradePartyId,
        services: services.map(service => ({
          ...service,
          iconUrl: service.icon_url,
          provider: {
            ...service.provider,
            logoUrl: service.provider.logo_url
          }
        })),
        imageFile: null,
        thumbnailFile: null,
        noteLabel,
        registrationForm: toCamelCaseObject(registrationForm),
        hasRegistrationForm: !!registrationForm,
        purchasingLimit,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt: toOptionalDateTime(expiredAt)
      };
      this.imageUrl = imageUrl;
      this.thumbnailUrl = thumbnailUrl;
    },
    async fetchData() {
      const { subscriptionPackageId: id } = this.$route.params;

      const response = await baanRueJaiClient.admin.subscriptions.recommend_packages.get(
        id
      );

      return response.data.result;
    },
    fetchTargets() {
      return Promise.resolve([
        { id: "0", name: "สำหรับบ้าน" },
        { id: "1", name: "สำหรับคอนโด" },
        { id: "2", name: "สำหรับบ้านขนาดใหญ่" }
      ]);
    },
    async fetchSellerTradeParties() {
      const response = await baanRueJaiClient.admin.subscriptions.receipts.getSellerTradeParties();

      return response.data.result;
    },
    async handleClickSave() {
      try {
        await this.updatePackage();
        this.$router.push({ name: "RuejaiClubSalePackageListPage" });
      } catch (err) {
        if (err.response.status === 400) {
          alert(err.response.data.message);
          console.error(err);
        } else {
          alert(err.message);
          console.error(err);
        }
      }
    },
    async updatePackage() {
      const { subscriptionPackageId: id } = this.$route.params;
      const {
        name,
        receiptName,
        detail,
        target,
        termsAndConditions,
        sellerTradePartyId,
        imageFile,
        thumbnailFile,
        noteLabel,
        registrationForm,
        purchasingLimit,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt
      } = this.form;
      const subpackageIds = packageCollection
        ? packageCollection.split(",").map(id => id.trim())
        : [];

      const params = {
        name,
        receiptName,
        detail,
        target,
        isSecret: false,
        termsAndConditions,
        sellerTradePartyId,
        noteLabel,
        registrationForm,
        purchasingLimit,
        purchaseSuccessTitle,
        purchaseSuccessMessage,
        expiredAt: toOptionalISOString(expiredAt)
      };

      if (imageFile) {
        params.imageUrl = await uploadPublicImage(imageFile, name, "article");
      }

      if (thumbnailFile) {
        params.thumbnailUrl = await uploadPublicImage(
          thumbnailFile,
          name,
          "article"
        );
      }

      const response = await baanRueJaiClient.admin.subscriptions.recommend_packages.put(
        id,
        params
      );

      return response;
    },
    async handleClickRemove() {
      if (!confirm("ยืนยันการลบบริการ")) {
        return;
      }

      try {
        await this.removePackage();
        this.$router.push({ name: "RuejaiClubSalePackageListPage" });
      } catch (err) {
        alert("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        console.error(err);
      }
    },
    async removePackage() {
      const { id, subscriptionPackageId } = this.$route.params;

      await baanRueJaiClient.admin.ruejaiClub.salePackages.delete(id);
      await baanRueJaiClient.admin.subscriptions.recommend_packages.delete(subscriptionPackageId);
    },
    handleClickDuplicate() {
      const { subscriptionPackageId: id } = this.$route.params;
      const createType = createPackageType.RUEJAICLUB;

      this.$router.push({ name: "PackageCreatePage", query: { id, createType }});
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: $space 0;
}

.editor {
  margin-bottom: $space;
}
</style>



// WEBPACK FOOTER //
// src/features/ruejai-club/modules/sale-package/pages/RuejaiClubSalePackageDetailPage.vue